import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, LinearProgress, Button, Skeleton } from '@mui/material';
import { Close, CheckCircleOutline } from '@mui/icons-material';
import { aiMatchingService, BulkEvaluationStatusResponse } from 'services/aiMatchingService';
import { toast } from 'react-toastify';

interface BulkEvaluationStatusBannerProps {
    activePromptVersionId: string;
    expertId: string;
    jobId: string;
    onDismiss?: () => void;
}

type EvalStatus = BulkEvaluationStatusResponse['output'] | null;

const POLL_INTERVAL = 5000;

const BulkEvaluationStatusBanner: React.FC<BulkEvaluationStatusBannerProps> = ({
    activePromptVersionId,
    expertId,
    jobId,
    onDismiss,
}) => {
    const [status, setStatus] = useState<EvalStatus>(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleCancel = async () => {
        try {
            setCancelling(true);
            await aiMatchingService.cancelBulkEvaluation({
                activePromptVersionId,
                expertId,
                bulkJobId: jobId,
            });
            toast.success('Evaluation cancelled successfully');
            // Refresh status after cancel
            await fetchStatus();
        } catch (err) {
            console.error('Error cancelling bulk evaluation:', err);
            toast.error('Failed to cancel evaluation');
        } finally {
            setCancelling(false);
        }
    };

    const fetchStatus = useCallback(async () => {
        try {
            const response: BulkEvaluationStatusResponse = await aiMatchingService.getBulkEvaluationStatus({
                activePromptVersionId,
                expertId,
            });

            if (response.apiStatus === 'SUCCESS') {
                setStatus(response.output);
                setError(false);
            } else {
                setError(true);
            }
        } catch (err) {
            console.error('Error fetching bulk evaluation status:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [activePromptVersionId, expertId]);

    useEffect(() => {
        // Initial fetch
        fetchStatus();

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [fetchStatus]);

    // Set up polling based on status
    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);

        if (status && (status.status === 'QUEUED' || status.status === 'PROCESSING')) {
            timerRef.current = setTimeout(() => {
                fetchStatus();
            }, POLL_INTERVAL);
        }

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [status, fetchStatus]);

    const commonContainerStyles = {
        mx: 1.5,
        mb: 1,
        px: 2.5,
        py: 1.5,
        bgcolor: 'white',
        borderRadius: 1.5,
        border: '1px solid',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    };

    if (loading && !status) {
        return (
            <Box sx={{ ...commonContainerStyles, borderColor: '#e3eaf6' }}>
                <Skeleton variant="circular" width={24} height={24} sx={{ flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="10%" height={24} />
                    </Box>
                    <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 3, mb: 0.5 }} />
                    <Skeleton variant="text" width="40%" height={18} />
                </Box>
            </Box>
        );
    }

    // Don't render if errored or no status data
    if (error || !status) return null;

    const isComplete = status.status === 'COMPLETED';
    const isFailed = status.status === 'FAILED';
    const isProcessing = status.status === 'PROCESSING' || status.status === 'QUEUED';

    const { progress } = status;
    const percent = Math.round(progress.percentComplete);

    return (
        <Box
            sx={{
                ...commonContainerStyles,
                borderColor: isComplete ? '#e0e0e0' : (isFailed ? '#f44336' : '#e3eaf6'),
            }}
        >
            {/* Status icon */}
            {isComplete || isFailed ? (
                <CheckCircleOutline
                    sx={{
                        color: isFailed ? '#f44336' : '#4caf50',
                        fontSize: 24,
                        flexShrink: 0,
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: 22,
                        height: 22,
                        border: '2.5px solid #1976d2',
                        borderTop: '2.5px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        flexShrink: 0,
                        '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                        },
                    }}
                />
            )}

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Title row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#333' }}>
                        {isComplete
                            ? 'Processing complete'
                            : isFailed
                                ? 'Processing failed'
                                : `Processing candidates... ${progress.processed}/${progress.total}`}
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ color: '#666' }}>
                        {percent}%
                    </Typography>
                </Box>

                {/* Progress bar */}
                <LinearProgress
                    variant="determinate"
                    value={percent}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#e8eef6',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            bgcolor: isComplete ? '#4caf50' : isFailed ? '#f44336' : '#1976d2',
                        },
                        mb: 0.5,
                    }}
                />

                {/* Stats row */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500 }}>
                        {progress.successful} completed
                    </Typography>
                    {progress.failed > 0 && (
                        <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 500 }}>
                            ⚠ {progress.failed} failed
                        </Typography>
                    )}
                    {isProcessing && progress.pending > 0 && (
                        <Typography variant="caption" sx={{ color: '#666' }}>
                            {progress.pending} pending
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Cancel button - show when processing */}
            {isProcessing && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancel}
                    disabled={cancelling}
                    sx={{
                        textTransform: 'none',
                        fontSize: '11px',
                        fontWeight: 600,
                        borderRadius: 1,
                        py: 0.3,
                        px: 1.5,
                        minHeight: 0,
                        flexShrink: 0,
                        borderColor: '#f44336',
                        color: '#f44336',
                        '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.04)', borderColor: '#d32f2f' },
                        '&.Mui-disabled': { borderColor: '#e0e0e0', color: '#bdbdbd' },
                    }}
                >
                    {cancelling ? 'Cancelling...' : 'Cancel'}
                </Button>
            )}

            {/* Dismiss button - show when complete or failed */}
            {(isComplete || isFailed) && (
                <IconButton
                    size="small"
                    onClick={onDismiss}
                    sx={{ flexShrink: 0, color: '#999' }}
                >
                    <Close fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
};

export default BulkEvaluationStatusBanner;
